import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Space,
  Steps,
  Typography,
  message,
} from "antd";
import { CopyOutlined, LinkOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  getHostedFormUrl,
  getPublicFormActionUrl,
  getPublicFormTokenUrl,
} from "../../../redux/actions/formsAction";
import { useFormDetail } from "./FormDetailContext";
import {
  formDetailPath,
  generateEmbedHtml,
  resolveFormFieldsForUi,
} from "./formUtils";

const { Paragraph, Text, Title } = Typography;

function CodeBlock({ children, onCopy, copyLabel = "Copy code" }) {
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="small">
      <pre
        style={{
          whiteSpace: "pre-wrap",
          fontSize: 12,
          background: "#fafafa",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: 12,
          margin: 0,
        }}
      >
        {children}
      </pre>
      {onCopy && (
        <Button icon={<CopyOutlined />} onClick={onCopy}>
          {copyLabel}
        </Button>
      )}
    </Space>
  );
}

export default function FormIntegration() {
  const { id, publicId, isNew, formRecord } = useFormDetail();
  const [activeKey, setActiveKey] = useState(["share"]);

  const actionUrl = publicId ? getPublicFormActionUrl(publicId) : "";
  const tokenUrl = publicId ? getPublicFormTokenUrl(publicId) : "";
  const hostedUrl = publicId ? getHostedFormUrl(publicId) : "";
  const fields = useMemo(
    () => resolveFormFieldsForUi(formRecord),
    [formRecord],
  );

  const htmlSnippet = useMemo(() => {
    if (!actionUrl || !tokenUrl) return "";
    return generateEmbedHtml({ fields, actionUrl, tokenUrl });
  }, [fields, actionUrl, tokenUrl]);

  const examplePayload = useMemo(() => {
    const body = { antiSpamToken: "TOKEN_FROM_/token" };
    for (const field of fields) {
      if (field.type === "email") body[field.name] = "user@company.com";
      else if (field.type === "phone") body[field.name] = "+15550000000";
      else if (field.type === "checkbox") body[field.name] = true;
      else body[field.name] = "Example value";
    }
    return body;
  }, [fields]);

  const jsSnippet = actionUrl
    ? `const formUrl = "${actionUrl}";
const tokenUrl = "${tokenUrl}";

const { token } = await fetch(tokenUrl).then((r) => r.json());

await fetch(formUrl, {
  method: "POST",
  headers: { Accept: "application/json", "Content-Type": "application/json" },
  body: JSON.stringify(${JSON.stringify(examplePayload, null, 2).replace(
      '"TOKEN_FROM_/token"',
      "token",
    )}),
});`
    : "";

  const phpSnippet = actionUrl
    ? `// Fetch this when the page loads, not right before forwarding — the
// token's timestamp is what proves a real visitor took time to fill the
// form. Stash it in the session (or a hidden field) alongside the page.
$token = json_decode(file_get_contents("${tokenUrl}"), true)["token"];

$payload = array_merge($_POST, ["antiSpamToken" => $token]);

$ch = curl_init("${actionUrl}");
curl_setopt_array($ch, [
  CURLOPT_POST => true,
  CURLOPT_POSTFIELDS => http_build_query($payload),
  CURLOPT_HTTPHEADER => ["Accept: application/json"],
  // Forward the visitor's real User-Agent, not curl's default (or none),
  // so the request isn't flagged as missing/bot user agent.
  CURLOPT_USERAGENT => $_SERVER["HTTP_USER_AGENT"] ?? "",
  CURLOPT_RETURNTRANSFER => true,
]);
curl_exec($ch);`
    : "";

  const nodeSnippet = actionUrl
    ? `// Fetch this when the page loads, not right before forwarding — the
// token's timestamp is what proves a real visitor took time to fill the
// form. Stash it in session/state alongside the rendered page.
const { token } = await fetch("${tokenUrl}").then((r) => r.json());

await fetch("${actionUrl}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    // Forward the visitor's real User-Agent so the request isn't flagged
    // as missing/bot user agent.
    "User-Agent": req.headers["user-agent"] || "",
  },
  body: JSON.stringify({ ...req.body, antiSpamToken: token }),
});`
    : "";

  const copyText = (text, label = "Copied") => {
    navigator.clipboard.writeText(text);
    message.success(label);
  };

  if (isNew || !publicId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Save your form first"
        description="Go to Settings, name your form, and click Save. Your share link and embed code will appear here."
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <div>
        <Title level={5} style={{ marginTop: 0 }}>
          Share or embed your form
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Send visitors to your hosted Mailinflow page, or paste embed code on
          your own site. New submissions show up in the Submissions tab.
        </Paragraph>
      </div>

      <Collapse
        activeKey={activeKey}
        onChange={setActiveKey}
        items={[
          {
            key: "share",
            label: "Share link (no website needed)",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Paragraph style={{ marginBottom: 0 }}>
                  Share this link in email, social posts, or QR codes. Visitors
                  fill out the form on Mailinflow — no coding required.
                </Paragraph>
                <Paragraph copyable={{ text: hostedUrl }} style={{ marginBottom: 0 }}>
                  <LinkOutlined /> {hostedUrl}
                </Paragraph>
                <Space wrap>
                  <Button onClick={() => copyText(hostedUrl, "Link copied")}>
                    Copy share link
                  </Button>
                  <Button href={hostedUrl} target="_blank">
                    Open hosted form
                  </Button>
                  <Link to={formDetailPath(id, "fields")}>Edit fields</Link>
                </Space>
              </Space>
            ),
          },
          {
            key: "website",
            label: "Website or page builder",
            children: (
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                <Paragraph style={{ marginBottom: 0 }}>
                  Generated from your{" "}
                  <Link to={formDetailPath(id, "fields")}>Fields</Link> tab.
                  Paste into an HTML block or custom code section.
                </Paragraph>
                <CodeBlock
                  onCopy={() => copyText(htmlSnippet, "Form code copied")}
                  copyLabel="Copy form code"
                >
                  {htmlSnippet}
                </CodeBlock>
              </Space>
            ),
          },
          {
            key: "app",
            label: "Custom app (React, Vue, mobile app)",
            children: (
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                <Paragraph style={{ marginBottom: 0 }}>
                  Fetch a token when the form screen loads, then POST the same
                  fields plus <Text code>antiSpamToken</Text>.
                </Paragraph>
                <CodeBlock
                  onCopy={() => copyText(jsSnippet, "Example copied")}
                  copyLabel="Copy example"
                >
                  {jsSnippet}
                </CodeBlock>
              </Space>
            ),
          },
          {
            key: "server",
            label: "Server-side forwarding (PHP, Node)",
            children: (
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="large"
              >
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  If your site posts to your own server first, forward to this
                  address: <Text code>{actionUrl}</Text>. Fetch a token from{" "}
                  <Text code>{tokenUrl}</Text> when the page loads (not right
                  before forwarding — the delay is what proves a real visitor
                  filled the form) and include it as{" "}
                  <Text code>antiSpamToken</Text>. Also forward the visitor's
                  original <Text code>User-Agent</Text> header — a missing or
                  script-default one gets flagged too.
                </Paragraph>
                <div>
                  <Text strong>PHP example</Text>
                  <CodeBlock
                    onCopy={() => copyText(phpSnippet, "PHP example copied")}
                  >
                    {phpSnippet}
                  </CodeBlock>
                </div>
                <div>
                  <Text strong>Node example</Text>
                  <CodeBlock
                    onCopy={() => copyText(nodeSnippet, "Node example copied")}
                  >
                    {nodeSnippet}
                  </CodeBlock>
                </div>
              </Space>
            ),
          },
        ]}
      />

      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: "Configure fields",
            description: "Use the Fields tab to choose what to collect.",
          },
          {
            title: "Share or embed",
            description: "Hosted link for quick sharing, or HTML for your site.",
          },
          {
            title: "Review submissions",
            description: "New responses appear in the Submissions tab.",
          },
        ]}
      />

      <Alert
        type="info"
        showIcon
        message="Spam protection is included"
        description="Hosted and embed flows load a security token automatically. You do not need extra setup."
      />

      <div>
        <Text strong>API submit address</Text>
        <Paragraph copyable={{ text: actionUrl }} style={{ marginBottom: 0 }}>
          {actionUrl}
        </Paragraph>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Technical endpoint used by the hosted page and embed code.
        </Text>
      </div>

      <Card size="small" title="Field names in this form">
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {fields.map((field) => (
            <li key={field.id || field.name}>
              <Text code>{field.name}</Text> — {field.label}
              {field.required ? " (required)" : ""}
            </li>
          ))}
        </ul>
        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          Custom fields appear in notifications as{" "}
          <Text code>{"{{fieldName}}"}</Text>. Set a thank-you redirect under
          Settings if needed.
        </Paragraph>
      </Card>
    </Space>
  );
}
